<template>
  <div class="container">
    <md-content v-if="caseConf" class="case-preview md-scrollbar gray-3 bg">
      <div class="header">
        <h3 class="case-name">{{ caseConf.name }}</h3>
        <div>
          <help-tooltip v-if="canEdit && caseConf.warningsCount" type="warning">{{ caseConf.warningsCount }} warning(s) found.</help-tooltip>
          <help-tooltip v-if="canEdit && caseConf.errorsCount" type="error">{{ caseConf.errorsCount }} error(s) found.</help-tooltip>
        </div>
        <md-button class="md-primary md-icon-button favorite-button" @click="toggleFavorite">
          <md-icon class="favorite-icon" :class="{ active: caseConf.isFavorite }" label="Favorite">star</md-icon>
        </md-button>
        <md-menu v-if="!isStudent">
          <md-button md-menu-trigger class="md-primary md-icon-button share-button">
            <md-icon class="share-icon" label="Share">share</md-icon>
          </md-button>
          <md-menu-content>
            <md-menu-item :href="casePdfLink" target="_blank">PDF Case Summary</md-menu-item>
          </md-menu-content>
        </md-menu>
      </div>
      
      <div v-if="canRead" class="summary">
        <div class="image-container">
          <div class="avatar image" :style="{'background-image': `url(${pictureFor(caseConf._avatar)})`}" />
          <span class="caption">{{ get(caseConf, '_avatar.name.en') }}</span>
        </div>
        <div class="image-container">
          <div class="environment image" :style="{'background-image': `url(${pictureFor(caseConf._startLocation)})`}" />
          <span class="caption">{{ get(caseConf, '_startLocation.name.en') }}</span>
        </div>
        <div v-if="caseConf._endLocation" class="image-container">
          <div class="environment image" :style="{'background-image': `url(${pictureFor(caseConf._endLocation)})`}" />
          <span class="caption">{{ get(caseConf, '_endLocation.name.en') }}</span>
        </div>
      </div>

      <div v-if="canRead" class="tags">
        <md-chip v-if="!caseConf.tags || caseConf.tags.length === 0" class="md-primary tag gray-2 bg">No tags</md-chip>
        <md-chip v-for="(tag, index) in caseConf.tags" :key="index" class="md-primary tag gray-6 bg">
          {{ tag.text }}
        </md-chip>
      </div>

      <div v-if="canRead" class="dispatch">
        <div v-if="caseConf.createdAt">
          <h4 class="title">Date Created</h4>
          <p class="content">{{ dateLabel(caseConf.createdAt) }}</p>
        </div>
        <h4 class="title">
          {{ 'Learning Objectives' | translateFeatureLabel }}
        </h4>
        <p class="content">{{ get(caseConf, 'dispatch.learningObjectives.caption.en') }}</p>
        <h4 class="title">Scenario Information</h4>
        <p class="content">{{ get(caseConf, 'dispatch.chiefComplaint.caption.en') }}</p>
      </div>

      <div v-if="!!caseConf.errorsCount" class="error-section">
        <h4 class="title">Update Required</h4>
        <p class="content">Open the case to view required edits.</p>
      </div>

      <div class="buttons">
        <div v-if="canRead" class="button-wrapper">
          <md-button class="md-raised md-primary grades" @click="openGrades()">
            Grades
          </md-button>
          <md-tooltip md-direction="top" md-delay="300">View the Grades of users who have previously completed this scenario</md-tooltip>
        </div>
        <div v-if="canShare" class="button-wrapper">
          <md-button class="md-raised md-primary sharing" @click="openSharing()">Sharing</md-button>
        </div>
      </div>

      <div v-if="canRun && caseAssignments.length > 0" class="section-name">
        Shared Version
      </div>

      <div class="buttons">
        <div v-if="canDelete" class="button-wrapper">
          <md-button :disabled="isAssignmentActive" class="md-raised md-accent delete" @click="openRemoveDialog()">
            Delete
          </md-button>
          <md-tooltip v-if="!isAssignmentActive" md-direction="top" md-delay="300">Permanently remove this scenario</md-tooltip>
          <md-tooltip v-if="isAssignmentActive" md-direction="top" md-delay="300">
            Cannot delete when associated with an active assignment
          </md-tooltip>
        </div>
        <div v-if="canDuplicate" class="button-wrapper">
          <md-button class="md-raised duplicate" :disabled="!!caseConf.errorsCount" @click="openDuplicateDialog()">
            Duplicate
          </md-button>
          <md-tooltip md-direction="top" md-delay="300">Create an exact copy of this scenario</md-tooltip>
        </div>
        <div v-if="canRun" class="button-wrapper">
          <md-button class="md-raised md-primary run" :disabled="!!caseConf.errorsCount" @click="tryRun()">Run</md-button>
          <md-tooltip md-direction="top" md-delay="300">
            <span v-if="!caseConf.errorsCount">Run this scenario in a new tab</span>
            <span v-else>Update Required</span>
          </md-tooltip>
        </div>
        <div v-if="canRead" class="button-wrapper">
          <md-button class="md-raised md-primary edit" @click="load()">{{ canEdit ? 'Edit' : 'View' }}</md-button>
        </div>
      </div>

      <div v-if="caseAssignments && caseAssignments.length" class="buttons">        
        <div
          v-for="assignment in caseAssignments"
          :key="assignment.id"
          class="assignment-outer"
        >
          <div class="section-name">{{ assignment.name }}</div>
          <div class="assignment-table">
            <span>Number of Attempts</span>
            <span>End Date</span>
            <span>End Time</span>
            <span class="gray-4 color">{{ computeAttempts(assignment) }}</span>
            <span class="gray-4 color">{{ assignment.endTime | formatDate }}</span>
            <span class="gray-4 color">{{ assignment.endTime | formatTime }}</span>
          </div>
          <div class="instructor-column">
            <span>Instructor</span>
            <span class="gray-4 color">{{ ownerLabel(assignment) }}</span>
          </div>
          <md-button class="md-raised md-primary run" @click="tryRun(assignment)">
            Run
            <md-tooltip md-direction="top" md-delay="300">Run this assignment in a new tab</md-tooltip>
          </md-button>
        </div>
      </div>      
    </md-content>
      
    <!-- Dialogs -->
    <md-dialog :md-active.sync="state.sharingDialogOpen">
      <md-dialog-title>Share With Others</md-dialog-title>

      <md-dialog-content>
        <sharing-table :case-conf="caseConf" @close="state.sharingDialogOpen = false" @update="update" />
      </md-dialog-content>
    </md-dialog>

    <md-dialog-confirm
      class="confirm-dialog"
      :md-active.sync="state.removeDialogOpen"
      md-title="Delete this case ?"
      md-content="Are you sure you want to permanently delete this case?"
      md-confirm-text="Accept"
      md-cancel-text="Cancel"
      @md-confirm="remove()"
    />

    <md-dialog 
      class="confirm-dialog"
      :md-active.sync="state.duplicateDialogOpen"
      :md-fullscreen="false"
    >
      <md-dialog-title>Duplicate this scenario ?</md-dialog-title>
      <md-dialog-content>        
        <md-field :class="{'md-invalid': state.duplicateError}">
          <label>Scenario Name</label>
          <md-input v-model="state.duplicateName" placeholder="New Scenario Name..." />
          <span class="md-error">{{ state.duplicateError }}</span>
        </md-field>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="state.duplicateDialogOpen = false">Cancel</md-button>
        <md-button class="md-primary" @click="duplicate(state.duplicateName)">Accept</md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog-alert
      :md-active.sync="state.noMoreAttemptsDialogOpen"
      md-title="Assignment"
      md-content="You have no more attempts for this assignment." 
    />
    
    <md-dialog-alert
      :md-active.sync="state.cannotRemoveDialogOpen"
      md-title="Delete Case"
      md-content="Cannot delete case when assignment is active." 
    />

    <md-dialog 
      class="confirm-dialog"
      :md-active.sync="state.browserWarningDialogOpen"
      :md-fullscreen="false"
    >
      <md-dialog-title>Error!</md-dialog-title>
      <md-dialog-content>
        <label>Google Chrome (64 bit) is required to run VRpatients. Please follow the link below to upgrade.</label>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="state.browserWarningDialogOpen = false">Cancel</md-button>
        <md-button class="md-primary" @click="run()">Run Anyway</md-button>
        <md-button class="md-primary" href="https://www.google.com/chrome/?platform=win64" target="_blank">Upgrade</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import get from 'lodash/get';
import intersection from 'lodash/intersection';
import UAParser from 'ua-parser-js';
import semverGt from 'semver/functions/gt';

import HelpTooltip from '~lib/common/components/HelpTooltip';
import { userLabel, dateLabel } from '~lib/common/utils/labels';

import SharingTable from '../../sharing/SharingTable';

export default {
  name: 'case-preview',
  components: {
    HelpTooltip,
    SharingTable,
  },
  props: {
    caseConf: {
      type: Object,
      default: undefined,
    },
    user: {
      type: Object,
      required: true,
    },
    idToken: {
      type: String,
      required: true,
    },
    caseAssignments: {
      type: Array,
      default: () => [],
    },
    attemptsByAssignment: {
      type: Object,
      default: () => {},
    },
    runFailure: {
      type: Boolean,
    },
    removeFailure: {
      type: Boolean,
    },
    isAssignmentActive: {
      type: Boolean,
      default: undefined,
    },
    orgData: {
      type: Object,
      default: undefined,
    },
    getAttemptsUsed: {
      type: Function,
      default: () => {},
    },
    getTotalAttempts: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      state: {
        removeDialogOpen: false,
        duplicateDialogOpen: false,
        sharingDialogOpen: false,
        browserWarningDialogOpen: false,
        noMoreAttemptsDialogOpen: false,
        cannotRemoveDialogOpen: false,
        assignmentToRun: undefined,
        duplicateName: '',
        duplicateError: undefined,
      },
      defaultPicture: 'http://icons.iconarchive.com/icons/papirus-team/papirus-status/128/image-missing-icon.png',
    };
  },
  computed: {
    isStudent() {
      return this.user['https://vrpatients.com/role'] === 'STUDENT';
    },
    canRun() {
      return this.hasPermission('run') || this.hasPermission('read');
    },
    canRead() {
      return !this.isStudent && this.hasPermission('read');
    },
    canDelete() {
      return !this.isStudent && this.hasPermission('write');
    },
    canDuplicate() {
      return !this.isStudent && this.hasPermission('read');
    },
    canEdit() {
      return !this.isStudent && this.hasPermission('write');
    },
    canShare() {
      return !this.isStudent && this.hasPermission('share');
    },
    isPlatformWebglSupported() {
      const uaparser = new UAParser(navigator.userAgent);
      const cpu = uaparser.getCPU();
      const os = uaparser.getOS();
      const browser = uaparser.getBrowser();
      if (os.name === 'Windows') {
        return cpu.architecture === 'amd64' && browser.name === 'Chrome';
      }
      // Mac OS user agents don't include cpu architecture in their user agent
      // but any version > 10.6 is necessarily running on a 64 bit processor
      // https://apple.stackexchange.com/questions/12666/how-to-check-whether-my-intel-based-mac-is-32-bit-or-64-bit
      if (os.name === 'Mac OS') {
        return (semverGt(os.version, '10.6.0') || cpu.architecture === 'amd64') && browser.name === 'Chrome';
      }
      return false;
    },
    casePdfLink() {
      return `${process.env.VUE_APP_CASES_SERVICE_URL}/pdf/${this.caseConf.id}?token=${this.idToken}`
    },
  },
  watch: {
    runFailure(newValue, oldValue) {
      // Show dialog on rising edge
      if (newValue && !oldValue) {
        this.state.noMoreAttemptsDialogOpen = true;
      }
    },
    removeFailure(newValue, oldValue) {
      // Show dialog on rising edge
      if (newValue && !oldValue) {
        this.state.cannotRemoveDialogOpen = true;
      }
    }
  },
  methods: {
    load() {
      this.$emit('load', this.caseConf);
      this.$analytics.event('Case Page', 'case_details_edit', this.caseConf.name);
    },
    openRemoveDialog() {
      this.state.removeDialogOpen = true;
      this.$analytics.event('Case Page', 'case_details_delete', this.caseConf.name);
    },
    openDuplicateDialog() {
      this.state.duplicateError = false;
      this.state.duplicateDialogOpen = true;
      this.$analytics.event('Case Page', 'case_details_duplicate', this.caseConf.name);
    },
    openBrowserWarningDialog() {
      this.state.browserWarningDialogOpen = true;
    },
    remove() {
      this.$emit('remove', this.caseConf);
    },
    duplicate(caseName) {
      if (!caseName) {
        this.state.duplicateError = 'You must choose a name for the duplicate';
      } else {
        this.state.duplicateName = '';
        this.state.duplicateDialogOpen = false;
        this.$emit('duplicate', this.caseConf, caseName);      
      }
    },
    tryRun(assignment) {
      this.state.assignmentToRun = assignment;
      if (this.isPlatformWebglSupported) {
        this.run();
      } else {
        this.$analytics.event('Case Page', 'run incompatible browser');
        this.openBrowserWarningDialog();
      }
    },
    run() {
      this.state.browserWarningDialogOpen = false;
      const orgId = this.user.groups[0];
      this.$emit('run', this.caseConf, orgId, this.state.assignmentToRun);
      this.$analytics.event('Case Page', 'case_details_run', this.caseConf.name);
    },
    openGrades() {
      this.$emit('openGrades', this.caseConf);
      this.$analytics.event('Case Page', 'case_details_grades', this.caseConf.name);
    },
    openSharing() {
      this.state.sharingDialogOpen = true;
      this.$analytics.event('Case Page', 'case_details_sharing', this.caseConf.name);
    },
    toggleFavorite() {
      this.$emit('favorite', this.caseConf);
    },
    pictureFor(item) {
      return get(item, 'picture.url', this.defaultPicture);
    },
    hasPermission(permission) {
      const userId = this.user.id;
      const userGroups = this.user.groups;
      const accessSpecification = get(
        this.caseConf,
        `access.${permission}`,
        {},
      );
      if (!accessSpecification) {
        return false;
      }
      return (
        (accessSpecification.users &&
          accessSpecification.users.includes(userId)) ||
        intersection(accessSpecification.groups, userGroups).length !== 0
      );
    },
    update(updates) {
      this.$emit('update', updates);
    },
    get,
    computeAttempts(assignment) {
      return `${this.getAttemptsUsed(assignment)} / ${this.getTotalAttempts(assignment)}`;
    },
    ownerLabel(assignment) {
      const ownerId = get(assignment, 'access.owner');
      const ownerUser = this.orgData.users.find(u => u.user_id === ownerId);
      return ownerUser ? userLabel(ownerUser) : '--';
    },
    dateLabel,
  },
};
</script>

<style scoped>
.container {
  min-width: 400px;
  width: 30%;
}
.case-preview {
  height: 100%;
  padding: 30px 35px 30px 10px;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.header, .summary, .tags {
  padding: 10px 0;
  border-bottom: solid 4px #004580;
}

.header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.case-name, .caption {
  font-style: normal;
  font-weight: 500;
  font-size: 28px;
  line-height: 1.2;
}

.case-name {
  margin: 0 auto 0 0;
  flex: 1 0 0;
}

.summary {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  flex-wrap: nowrap;
  /* for the captions */
}

.image-container {
  width: 100px;
  text-align: center;
  margin: 10px;
  position: relative;
  flex-grow: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
}
.image-container .image {
  width: 100px;
  height: 100px;
  background-size: auto 100%;
  background-repeat: no-repeat;
  box-shadow: 1px 1px 4px 0 rgba(0,0,0,.4);
}

.image-container:first-child {
  margin-left: 0;
}

.image-container:last-child {
  margin-right: 0;
}

.avatar {
  background-size: cover;
  border-radius: 50%;
}

.environment {
  border-radius: 15px;
}

.image-container .caption {
  display: block;
  margin-top: 5px;
  position: relative;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  font-size: 14px;
}


@media (min-width: 1200px) {
  .image-container .caption {
    font-size: 16px;
  }
}

@media (min-width: 1500px) {
  .image-container .caption {
    font-size: 18px;
  }
}

@media (min-width: 1800px) {
  .image-container .caption {
    font-size: 20px;
  }
}


.md-chip.tag {
  margin: 2px;
}
.tags {
  flex: 1 0 auto;
  max-height: fit-content;
}

.dispatch .title {
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 20px;
  text-transform: uppercase;
  margin: 0;
  margin-top: 20px;
}

.dispatch .content {
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 20px;
  margin: 8px 0;
  white-space: pre-wrap;
}

.error-section {
  color: #f92c2d;
}

.error-section .title {
  font-size: 18px;
  text-transform: uppercase;
}

.error-section .content {
  font-size: 18px;
}

.buttons {
  flex-direction: row;
  justify-content: flex-end;
  border-bottom: none;
  float: right;
  margin-top: 15px;
}
.buttons .md-button {
  text-transform: uppercase;
}

.button-wrapper {
  display: inline-block; 
  width: 88px;
  margin-right: 8px;
}

.md-button.duplicate:not([disabled]) {
  color: white !important;
  background-color: #face14 !important;
}

.md-button:first-child {
  margin-left: 0;
}

.favorite-button,
.share-button {
  min-width: unset;
  border-radius: 50%;
}
.favorite-icon,
.share-icon {
  color: #5F5F5F !important;
  font-size: 24px !important;
}
.favorite-icon.active {
  color: #FFB800 !important;
}

.assignment-outer {
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.assignment-outer .md-button {
  margin-left: 0;
}

.section-name {  
  font-style: normal;
  font-weight: 500;
  font-size: 28px;
  line-height: 1.2;
  border-bottom: solid 4px #004580;
  align-self: stretch;
  padding-bottom: 5px;
}

.assignment-table {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  row-gap: 2px;
  column-gap: 10px;
  margin-top: 15px;
}

.instructor-column {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
}

</style>

<style>
.case-preview .buttons .md-button-content {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  width: 100%;
  height: 100%;
}
</style>